
import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    Divider,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    ListItemButton,
    ListItemText,
    CircularProgress,
    Popover,
} from '@mui/material';
import {
    Add as AddIcon,
    WbSunny as MyDayIcon,
    StarBorder as StarBorderIcon,
    Star as StarFilledIcon,
    CalendarToday as PlannedIcon,
    Assignment as TasksIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    CheckCircle as CheckCircleIcon,
    RadioButtonUnchecked as UncheckedIcon,
} from '@mui/icons-material';
import client from '../../api/client';

const TaskPage = () => {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedList, setSelectedList] = useState('Tasks');
    const [newTask, setNewTask] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTask, setSelectedTask] = useState(null); // Used for Details Modal
    
    // Popover States
    const [anchorEl, setAnchorEl] = useState(null);
    const [popoverTaskName, setPopoverTaskName] = useState('');

    const fetchTasks = async () => {
        try {
            const res = await client.get('/tasks');
            if (res.data.success) {
                setTasks(res.data.data);
            }
        } catch (err) {
            console.error('Error fetching tasks:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTasks();
    }, []);

    const handleAddTask = async (taskNameOverride) => {
        const title = taskNameOverride || newTask;
        if (!title.trim()) return null;
        try {
            const res = await client.post('/tasks', {
                title: title.trim(),
                list_name: selectedList === 'Important' || selectedList === 'Planned' ? 'Tasks' : selectedList,
                is_important: selectedList === 'Important',
                due_date: selectedList === 'Planned' ? new Date().toISOString().split('T')[0] : null
            });
            if (res.data.success) {
                setTasks([res.data.data, ...tasks]);
                if (!taskNameOverride) setNewTask('');
                return res.data.data;
            }
        } catch (err) {
            console.error('Error adding task:', err);
        }
        return null;
    };

    const handleAddIconClick = async (event) => {
        if (newTask.trim()) {
            const created = await handleAddTask();
            if (created) setSelectedTask(created);
        } else {
            // Open Popover instead of prompt
            setAnchorEl(event.currentTarget);
            setPopoverTaskName('');
        }
    };

    const toggleComplete = async (task) => {
        try {
            const res = await client.put(`/tasks/${task.id}`, { is_completed: !task.is_completed });
            if (res.data.success) {
                setTasks(tasks.map(t => t.id === task.id ? res.data.data : t));
            }
        } catch (err) {
            console.error('Error updating task:', err);
        }
    };

    const toggleImportant = async (task) => {
        try {
            const res = await client.put(`/tasks/${task.id}`, { is_important: !task.is_important });
            if (res.data.success) {
                setTasks(tasks.map(t => t.id === task.id ? res.data.data : t));
            }
        } catch (err) {
            console.error('Error updating task:', err);
        }
    };

    const handleDeleteTask = async (id) => {
        try {
            const res = await client.delete(`/tasks/${id}`);
            if (res.data.success) {
                setTasks(tasks.filter(t => t.id !== id));
            }
        } catch (err) {
            console.error('Error deleting task:', err);
        }
    };

    const lists = [
        { id: 'Tasks', icon: <TasksIcon />, color: '#6a1b9a' },
        { id: 'Important', icon: <StarBorderIcon />, color: '#d32f2f' },
        { id: 'Planned', icon: <PlannedIcon />, color: '#0288d1' },
    ];

    const getFilteredTasks = () => {
        let filtered = tasks || [];
        if (searchQuery) {
            filtered = filtered.filter(t => t.title && t.title.toLowerCase().includes(searchQuery.toLowerCase()));
        }

        // Failsafe completed check considering mysql booleans as TinyInt or Buffer
        const isNotCompleted = (t) => {
            if (t.is_completed === 1 || t.is_completed === true || t.is_completed === '1') return false;
            if (t.is_completed && t.is_completed.type === 'Buffer' && t.is_completed.data && t.is_completed.data[0] === 1) return false;
            return true; 
        };

        switch (selectedList) {
            case 'Important':
                return filtered.filter(t => t.is_important && isNotCompleted(t));
            case 'Planned':
                return filtered.filter(t => t.due_date && isNotCompleted(t));
            case 'Tasks':
            default:
                return filtered.filter(t => isNotCompleted(t));
        }
    };

    const isCompletedSafe = (t) => {
        if (t.is_completed === 1 || t.is_completed === true || t.is_completed === '1') return true;
        if (t.is_completed && t.is_completed.type === 'Buffer' && t.is_completed.data && t.is_completed.data[0] === 1) return true;
        return false;
    };

    const getCompletedTasks = () => {
        let filtered = (tasks || []).filter(t => isCompletedSafe(t));
        if (searchQuery) {
            filtered = filtered.filter(t => t.title && t.title.toLowerCase().includes(searchQuery.toLowerCase()));
        }
        return filtered;
    };

    return (
        <Box sx={{ display: 'flex', height: 'calc(100vh - 80px)', bgcolor: '#f5f5f5' }}>
            {/* Sidebar */}
            <Paper elevation={0} sx={{ width: 280, borderRight: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Tasks</Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
                    />
                </Box>
                <List>
                    {lists.map(list => (
                        <ListItemButton
                            key={list.id}
                            selected={selectedList === list.id}
                            onClick={() => setSelectedList(list.id)}
                            sx={{
                                '&.Mui-selected': { bgcolor: `${list.color}10`, color: list.color },
                                '&.Mui-selected .MuiListItemIcon-root': { color: list.color }
                            }}
                        >
                            <ListItemIcon>{list.icon}</ListItemIcon>
                            <ListItemText primary={list.id} />
                            <Typography variant="caption">
                                {(tasks || []).filter(t => {
                                    const notComplete = !isCompletedSafe(t);
                                    if (list.id === 'Tasks') return notComplete;
                                    if (list.id === 'Important') return t.is_important && notComplete;
                                    return t.due_date && notComplete;
                                }).length}
                            </Typography>
                        </ListItemButton>
                    ))}
                </List>
            </Paper>

            {/* Main Area */}
            <Box sx={{ flexGrow: 1, p: 4, overflowY: 'auto' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 4, color: lists.find(l => l.id === selectedList)?.color || 'inherit' }}>
                    {selectedList}
                </Typography>

                <Box sx={{ 
                    mb: 4, 
                    display: 'flex', 
                    alignItems: 'center', 
                    bgcolor: 'rgba(255, 255, 255, 0.5)', 
                    borderRadius: '8px',
                    p: '8px 16px',
                    border: '1px solid transparent',
                    color: 'text.secondary',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.8)'
                    },
                    '&:focus-within': {
                        bgcolor: '#ffffff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        border: '1px solid #e0e0e0',
                        color: 'primary.main'
                    }
                }}>
                    <IconButton size="small" onClick={handleAddIconClick} sx={{ mr: 1, color: 'inherit' }}>
                        <AddIcon />
                    </IconButton>
                    <TextField
                        fullWidth
                        placeholder="Add a task"
                        variant="standard"
                        value={newTask}
                        onChange={(e) => setNewTask(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddTask()}
                        InputProps={{
                            disableUnderline: true,
                            style: { color: 'inherit' }
                        }}
                    />
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                ) : (
                    <>
                        {getFilteredTasks().length > 0 && (
                            <List sx={{ bgcolor: 'white', borderRadius: '8px', border: '1px solid #e0e0e0', mb: 2 }}>
                                {getFilteredTasks().map(task => (
                                    <ListItem
                                        key={task.id}
                                        secondaryAction={
                                            <Box>
                                                <IconButton onClick={() => toggleImportant(task)}>
                                                    {task.is_important ? <StarFilledIcon sx={{ color: '#d32f2f' }} /> : <StarBorderIcon />}
                                                </IconButton>
                                                <IconButton onClick={() => handleDeleteTask(task.id)}><DeleteIcon /></IconButton>
                                            </Box>
                                        }
                                        sx={{ borderBottom: '1px solid #f0f0f0' }}
                                    >
                                        <ListItemIcon>
                                            <IconButton onClick={() => toggleComplete(task)}>
                                                <UncheckedIcon />
                                            </IconButton>
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary={task.title} 
                                            secondary={task.due_date ? `Due ${new Date(task.due_date).toLocaleDateString()}` : null} 
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => setSelectedTask(task)}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        )}

                        {getCompletedTasks().length > 0 && (
                            <>
                                <Typography variant="subtitle2" sx={{ mt: 4, mb: 2, color: 'text.secondary' }}>Completed</Typography>
                                <List sx={{ bgcolor: '#fafafa', borderRadius: '8px', border: '1px solid #e0e0e0', opacity: 0.7 }}>
                                    {getCompletedTasks().map(task => (
                                        <ListItem
                                            key={task.id}
                                            secondaryAction={<IconButton onClick={() => handleDeleteTask(task.id)}><DeleteIcon /></IconButton>}
                                            sx={{ borderBottom: '1px solid #f0f0f0' }}
                                        >
                                            <ListItemIcon>
                                                <IconButton onClick={() => toggleComplete(task)}>
                                                    <CheckCircleIcon color="success" />
                                                </IconButton>
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={task.title}
                                                sx={{ textDecoration: 'line-through', cursor: 'pointer' }}
                                                onClick={() => setSelectedTask(task)}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </>
                        )}
                    </>
                )}
                
                {/* Task Detail Dialog */}
                <Dialog open={!!selectedTask} onClose={() => setSelectedTask(null)} fullWidth maxWidth="sm">
                    <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        Task Details
                        <IconButton onClick={() => setSelectedTask(null)} size="small">
                            {/* Close Icon not imported, just using text for simplicity */}
                            X
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers>
                        {selectedTask && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 1 }}>
                                <TextField 
                                    label="Task Name" 
                                    fullWidth 
                                    variant="outlined"
                                    value={selectedTask.title} 
                                    onChange={(e) => setSelectedTask({...selectedTask, title: e.target.value})} 
                                />
                                
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField 
                                        type="date"
                                        label="Due Date"
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        value={selectedTask.due_date ? selectedTask.due_date.split('T')[0] : ''}
                                        onChange={(e) => setSelectedTask({...selectedTask, due_date: e.target.value || null})}
                                    />
                                </Box>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: 2, pt: 1 }}>
                        <Button color="inherit" onClick={() => setSelectedTask(null)}>Cancel</Button>
                        <Button variant="contained" onClick={async () => {
                            if (!selectedTask.title.trim()) return;
                            try {
                                const res = await client.put(`/tasks/${selectedTask.id}`, { 
                                    title: selectedTask.title,
                                    due_date: selectedTask.due_date
                                });
                                if (res.data.success) {
                                    setTasks(tasks.map(t => t.id === selectedTask.id ? res.data.data : t));
                                    setSelectedTask(null);
                                }
                            } catch (e) { 
                                console.error('Error updating task:', e); 
                            }
                        }}>Save</Button>
                    </DialogActions>
                </Dialog>

                {/* Popover for Quick Add (replaces window.prompt) */}
                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    PaperProps={{
                        sx: { mt: 1, borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }
                    }}
                >
                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: '280px' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>Create New Task</Typography>
                        <TextField
                            autoFocus
                            size="small"
                            placeholder="Enter task name"
                            value={popoverTaskName}
                            onChange={(e) => setPopoverTaskName(e.target.value)}
                            onKeyPress={async (e) => {
                                if (e.key === 'Enter' && popoverTaskName.trim()) {
                                    setAnchorEl(null);
                                    const created = await handleAddTask(popoverTaskName);
                                    if (created) setSelectedTask(created);
                                }
                            }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                            <Button size="small" color="inherit" onClick={() => setAnchorEl(null)}>Cancel</Button>
                            <Button 
                                variant="contained" 
                                size="small"
                                disabled={!popoverTaskName.trim()}
                                onClick={async () => {
                                    setAnchorEl(null);
                                    const created = await handleAddTask(popoverTaskName);
                                    if (created) setSelectedTask(created);
                                }}
                            >
                                Create
                            </Button>
                        </Box>
                    </Box>
                </Popover>
            </Box>
        </Box>
    );
};

export default TaskPage;
